
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Plus, X, ArrowRight, ArrowLeft, Check, Settings, Layers, Building2, Workflow, Cog, GripVertical, Filter } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDepartments } from '@/hooks/useDepartments';
import { useWorkItemTemplates } from '@/hooks/useWorkItemTemplates';
import { useCreateWorkflow, useUpdateWorkflow } from '@/hooks/useWorkflows';
import { ConditionRules, ConditionType } from '@/types/workflow';
import { ConditionBuilder } from '@/components/workflow/ConditionBuilder';

interface WorkflowWizardStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
}

interface WorkflowFormData {
  name: string;
  description: string;
  workflow_steps: Array<{
    id: string;
    name: string;
    description: string;
    step_number: number;
    workflow_step_departments: Array<{
      id?: string;
      department_id: string;
      department_name: string;
      is_required: boolean;
      execution_order: number;
      work_items?: Array<{
        id?: string;
        template_id: string;
        template_name: string;
        template_description?: string;
        execution_order: number;
        is_conditional?: boolean;
        condition_rules?: ConditionRules;
      }>;
    }>;
  }>;
}

const wizardSteps: WorkflowWizardStep[] = [
  {
    id: 'basic',
    title: '基本資訊',
    description: '設定工作流程名稱和描述',
    icon: Settings
  },
  {
    id: 'stages',
    title: '階段設定',
    description: '選擇或創建工作流程階段',
    icon: Layers
  },
  {
    id: 'departments',
    title: '部門配置',
    description: '為每個階段設定負責部門',
    icon: Building2
  },
  {
    id: 'preview',
    title: '預覽確認',
    description: '檢視並確認工作流程設定',
    icon: Check
  }
];

interface WorkflowCreationWizardProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  editMode?: boolean;
  initialData?: WorkflowFormData;
  workflowId?: string;
}

export const WorkflowCreationWizard: React.FC<WorkflowCreationWizardProps> = ({ 
  onSuccess, 
  onCancel,
  editMode = false,
  initialData,
  workflowId
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<WorkflowFormData>(
    initialData || {
      name: '',
      description: '',
      workflow_steps: []
    }
  );

  const { data: availableDepartments, isLoading: departmentsLoading } = useDepartments();
  
  const createWorkflowMutation = useCreateWorkflow({
    onSuccess: () => {
      onSuccess?.();
    }
  });

  const updateWorkflowMutation = useUpdateWorkflow({
    onSuccess: () => {
      onSuccess?.();
    }
  });

  const handleBasicInfoSubmit = (data: { name: string; description: string }) => {
    setFormData(prev => ({
      ...prev,
      name: data.name,
      description: data.description
    }));
    setCurrentStep(1);
  };

  const handleStepAdd = (stepData: { id: string; name: string; description: string }) => {
    const newStep = {
      id: stepData.id,
      name: stepData.name,
      description: stepData.description,
      step_number: formData.workflow_steps.length + 1,
      workflow_step_departments: []
    };
    
    setFormData(prev => ({
      ...prev,
      workflow_steps: [...prev.workflow_steps, newStep]
    }));
  };

  // 處理步驟拖拽排序
  const handleStepReorder = (activeId: string, overId: string) => {
    setFormData(prev => {
      const oldIndex = prev.workflow_steps.findIndex(step => step.id === activeId);
      const newIndex = prev.workflow_steps.findIndex(step => step.id === overId);
      
      const reorderedSteps = arrayMove(prev.workflow_steps, oldIndex, newIndex).map((step, index) => ({
        ...step,
        step_number: index + 1
      }));
      
      return {
        ...prev,
        workflow_steps: reorderedSteps
      };
    });
  };



  const handleDepartmentAdd = (stepId: string, departmentData: { id: string; name: string; isRequired: boolean }) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map(step => 
        step.id === stepId 
          ? {
              ...step,
              workflow_step_departments: [...step.workflow_step_departments, {
                department_id: departmentData.id,
                department_name: departmentData.name,
                is_required: departmentData.isRequired,
                execution_order: step.workflow_step_departments.length + 1,
                work_items: [] // Initialize empty work items array
              }]
            }
          : step
      )
    }));
  };

  const handleStepRemove = (stepId: string) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps
        .filter(step => step.id !== stepId)
        .map((step, index) => ({
          ...step,
          step_number: index + 1
        }))
    }));
  };

  const handleDepartmentRemove = (stepId: string, deptId: string) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map(step => 
        step.id === stepId 
          ? {
              ...step,
              workflow_step_departments: step.workflow_step_departments
                .filter(dept => dept.department_id !== deptId)
            }
          : step
      )
    }));
  };

  const handleExecutionOrderChange = (stepId: string, deptId: string, newOrder: number) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map(step => 
        step.id === stepId 
          ? {
              ...step,
              workflow_step_departments: step.workflow_step_departments.map(dept => 
                dept.department_id === deptId 
                  ? { ...dept, execution_order: newOrder }
                  : dept
              )
            }
          : step
      )
    }));
  };

  const handleWorkItemAdd = (stepId: string, deptId: string, workItemData: { id: string; name: string; description?: string }) => {
    try {
      setFormData(prev => {
        const newData = {
          ...prev,
          workflow_steps: prev.workflow_steps.map(step => 
            step.id === stepId 
              ? {
                  ...step,
                  workflow_step_departments: step.workflow_step_departments.map(dept => 
                    dept.department_id === deptId 
                      ? {
                          ...dept,
                          work_items: [...(dept.work_items || []), {
                            template_id: workItemData.id,
                            template_name: workItemData.name,
                            template_description: workItemData.description,
                            execution_order: (dept.work_items?.length || 0) + 1,
                            is_conditional: false,
                            condition_rules: undefined
                          }]
                        }
                      : dept
                  )
                }
              : step
          )
        };
        
        return newData;
      });
    } catch (error) {
      console.error('Error in handleWorkItemAdd:', error);
    }
  };

  const handleWorkItemRemove = (stepId: string, deptId: string, templateId: string) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map(step => 
        step.id === stepId 
          ? {
              ...step,
              workflow_step_departments: step.workflow_step_departments.map(dept => 
                dept.department_id === deptId 
                  ? {
                      ...dept,
                      work_items: (dept.work_items || [])
                        .filter(item => item.template_id !== templateId)
                        .map((item, index) => ({ ...item, execution_order: index + 1 }))
                    }
                  : dept
              )
            }
          : step
      )
    }));
  };

  const handleWorkItemReorder = (stepId: string, deptId: string, activeId: string, overId: string) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map(step => 
        step.id === stepId 
          ? {
              ...step,
              workflow_step_departments: step.workflow_step_departments.map(dept => 
                dept.department_id === deptId 
                  ? {
                      ...dept,
                      work_items: (() => {
                        const workItems = dept.work_items || [];
                        const oldIndex = workItems.findIndex(item => item.template_id === activeId);
                        const newIndex = workItems.findIndex(item => item.template_id === overId);
                        
                        if (oldIndex !== -1 && newIndex !== -1) {
                          const reorderedItems = arrayMove(workItems, oldIndex, newIndex);
                          return reorderedItems.map((item, index) => ({ ...item, execution_order: index + 1 }));
                        }
                        return workItems;
                      })()
                    }
                  : dept
              )
            }
          : step
      )
    }));
  };

  const handleWorkItemConditionToggle = (stepId: string, deptId: string, templateId: string, isConditional: boolean) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map(step => 
        step.id === stepId 
          ? {
              ...step,
              workflow_step_departments: step.workflow_step_departments.map(dept => 
                dept.department_id === deptId 
                  ? {
                      ...dept,
                      work_items: (dept.work_items || []).map(item => 
                        item.template_id === templateId 
                          ? { 
                              ...item, 
                              is_conditional: isConditional,
                              condition_rules: isConditional && !item.condition_rules 
                                ? { type: ConditionType.ALL, conditions: [] } 
                                : item.condition_rules
                            }
                          : item
                      )
                    }
                  : dept
              )
            }
          : step
      )
    }));
  };

  const handleWorkItemConditionUpdate = (stepId: string, deptId: string, templateId: string, conditionRules: ConditionRules | undefined) => {
    setFormData(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map(step => 
        step.id === stepId 
          ? {
              ...step,
              workflow_step_departments: step.workflow_step_departments.map(dept => 
                dept.department_id === deptId 
                  ? {
                      ...dept,
                      work_items: (dept.work_items || []).map(item => 
                        item.template_id === templateId 
                          ? { 
                              ...item, 
                              is_conditional: !!conditionRules,
                              condition_rules: conditionRules
                            }
                          : item
                      )
                    }
                  : dept
              )
            }
          : step
      )
    }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <BasicInfoStep onSubmit={handleBasicInfoSubmit} initialData={formData} />;
      case 1:
        return <StepsStep 
          workflow_steps={formData.workflow_steps} 
          onStepAdd={handleStepAdd}
          onStepReorder={handleStepReorder}
          onStepRemove={handleStepRemove}
          onNext={() => setCurrentStep(2)}
          onPrevious={() => setCurrentStep(0)}
        />;
      case 2:
        return <DepartmentsStep 
          workflow_steps={formData.workflow_steps}
          availableDepartments={availableDepartments || []}
          departmentsLoading={departmentsLoading}
          onDepartmentAdd={handleDepartmentAdd}
          onDepartmentRemove={handleDepartmentRemove}
          onExecutionOrderChange={handleExecutionOrderChange}
          onWorkItemAdd={handleWorkItemAdd}
          onWorkItemRemove={handleWorkItemRemove}
          onWorkItemReorder={handleWorkItemReorder}
          onWorkItemConditionToggle={handleWorkItemConditionToggle}
          onWorkItemConditionUpdate={handleWorkItemConditionUpdate}
          onNext={() => setCurrentStep(3)}
          onPrevious={() => setCurrentStep(1)}
        />;
      case 3:
        return <PreviewStep 
          formData={formData}
          editMode={editMode}
          isSubmitting={editMode ? updateWorkflowMutation.isPending : createWorkflowMutation.isPending}
          onSubmit={() => {
            if (editMode && workflowId) {
              // Simple update payload - backend handles deletions automatically
              // Just send the current steps, backend will delete what's not included
              const updatePayload = {
                name: formData.name,
                description: formData.description,
                workflow_steps: formData.workflow_steps.map((step, index) => ({
                  // Only include id if it's a valid UUID (existing item)
                  ...(step.id && step.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) 
                    ? { id: step.id } 
                    : {}),
                  name: step.name,
                  description: step.description,
                  stepNumber: index + 1,
                  workflowStepDepartments: step.workflow_step_departments.map((dept) => ({
                    // Only include id if it exists (existing department)
                    ...(dept.id ? { id: dept.id } : {}),
                    departmentId: dept.department_id,
                    departmentName: dept.department_name,
                    executionOrder: dept.execution_order,
                    isRequired: dept.is_required,
                    workItems: (dept.work_items || []).map((workItem) => ({
                      // Only include id if it exists (existing work item)
                      ...(workItem.id ? { id: workItem.id } : {}),
                      templateId: workItem.template_id,
                      templateName: workItem.template_name,
                      templateDescription: workItem.template_description,
                      executionOrder: workItem.execution_order,
                      isConditional: workItem.is_conditional,
                      conditionRules: workItem.condition_rules
                    }))
                  }))
                }))
              };
              console.log('Update workflow payload:', JSON.stringify(updatePayload, null, 2));
              updateWorkflowMutation.mutate({ id: workflowId, data: updatePayload as any });
            } else {
              // Create new workflow payload
              const createPayload = {
                name: formData.name,
                description: formData.description,
                workflowSteps: formData.workflow_steps.map((step, index) => ({
                  name: step.name,
                  description: step.description,
                  stepNumber: index + 1,
                  workflowStepDepartments: step.workflow_step_departments.map((dept) => ({
                    departmentId: dept.department_id,
                    departmentName: dept.department_name,
                    executionOrder: dept.execution_order,
                    isRequired: dept.is_required,
                    workItems: (dept.work_items || []).map((workItem) => ({
                      templateId: workItem.template_id,
                      templateName: workItem.template_name,
                      templateDescription: workItem.template_description,
                      executionOrder: workItem.execution_order,
                      isConditional: workItem.is_conditional,
                      conditionRules: workItem.condition_rules
                    }))
                  }))
                }))
              };
              console.log('Create workflow payload:', JSON.stringify(createPayload, null, 2));
              createWorkflowMutation.mutate(createPayload);
            }
          }} 
          onPrevious={() => setCurrentStep(2)}
        />;
      default:
        return null;
    }
  };

  if (departmentsLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Workflow className="h-5 w-5" />
              創建新工作流程
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-gray-600">載入部門資料中...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* 步驟導航 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="h-5 w-5" />
            創建新工作流程
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-6">
            {wizardSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center space-x-2 ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-blue-100 text-blue-600' : 
                      isCompleted ? 'bg-green-100 text-green-600' : 
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <div className="hidden md:block">
                      <div className="font-medium">{step.title}</div>
                      <div className="text-sm text-gray-500">{step.description}</div>
                    </div>
                  </div>
                  {index < wizardSteps.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-gray-300 ml-4" />
                  )}
                </div>
              );
            })}
          </div>
          
          <Separator className="mb-6" />
          
          {/* 步驟內容 */}
          {renderStepContent()}
        </CardContent>
      </Card>
    </div>
  );
};

// 基本資訊步驟
const BasicInfoStep: React.FC<{
  onSubmit: (data: { name: string; description: string }) => void;
  initialData: { name: string; description: string };
}> = ({ onSubmit, initialData }) => {
  const [name, setName] = useState(initialData.name);
  const [description, setDescription] = useState(initialData.description);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, description });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">工作流程名稱 *</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="例如：訂單處理流程"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="description">流程描述</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="描述此工作流程的用途和適用場景"
          rows={3}
        />
      </div>
      
      <div className="flex justify-end">
        <Button type="submit">
          下一步 <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </form>
  );
};

// 可拖拽步驟項目組件
const SortableStepItem: React.FC<{
  step: WorkflowFormData['workflow_steps'][0];
  index: number;
  onRemove: (id: string) => void;
}> = ({ step, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 border rounded-lg bg-white"
    >
      <div className="flex items-center gap-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab hover:cursor-grabbing text-gray-400"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <Badge variant="outline">{index + 1}</Badge>
        <div>
          <div className="font-medium">{step.name}</div>
          <div className="text-sm text-gray-500">{step.description}</div>
        </div>
      </div>
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => onRemove(step.id)}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// 步驟設定步驟
const StepsStep: React.FC<{
  workflow_steps: WorkflowFormData['workflow_steps'];
  onStepAdd: (step: { id: string; name: string; description: string }) => void;
  onStepReorder: (activeId: string, overId: string) => void;
  onStepRemove: (stepId: string) => void;
  onNext: () => void;
  onPrevious: () => void;
}> = ({ workflow_steps, onStepAdd, onStepReorder, onStepRemove, onNext, onPrevious }) => {
  const [newStepName, setNewStepName] = useState('');
  const [newStepDescription, setNewStepDescription] = useState('');

  // 設置拖拽感測器
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 處理拖拽結束
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onStepReorder(active.id as string, over.id as string);
    }
  };

  // 移除步驟
  const handleRemoveStep = (stepId: string) => {
    // Call parent component's remove function
    onStepRemove(stepId);
  };

  const handleCreateNewStep = () => {
    if (newStepName.trim()) {
      const newStep = {
        id: `step-${Date.now()}`,
        name: newStepName.trim(),
        description: newStepDescription.trim()
      };
      onStepAdd(newStep);
      setNewStepName('');
      setNewStepDescription('');
    }
  };

  return (
    <div className="space-y-6">
      {/* 創建新步驟 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">創建工作流程步驟</CardTitle>
          <p className="text-sm text-muted-foreground">為工作流程添加步驟，每個步驟可以包含多個部門</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="stepName">步驟名稱 *</Label>
              <Input
                id="stepName"
                placeholder="例如：裁剪、組裝、品檢"
                value={newStepName}
                onChange={(e) => setNewStepName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="stepDescription">步驟描述</Label>
              <Input
                id="stepDescription"
                placeholder="簡短描述此步驟的作用"
                value={newStepDescription}
                onChange={(e) => setNewStepDescription(e.target.value)}
              />
            </div>
          </div>
          <Button 
            onClick={handleCreateNewStep}
            disabled={!newStepName.trim()}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            添加步驟
          </Button>
        </CardContent>
      </Card>

      {/* 已選步驟列表 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">已選步驟 - 可拖拽排序</CardTitle>
        </CardHeader>
        <CardContent>
          {workflow_steps.length === 0 ? (
            <p className="text-gray-500 text-center py-8">尚未選擇任何步驟</p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={workflow_steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {workflow_steps.map((step, index) => (
                    <SortableStepItem
                      key={step.id}
                      step={step}
                      index={index}
                      onRemove={handleRemoveStep}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* 導航按鈕 */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          上一步
        </Button>
        <Button onClick={onNext} disabled={workflow_steps.length === 0}>
          下一步 <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// 可拖拽工作項目組件
const SortableWorkItemItem: React.FC<{
  workItem: NonNullable<WorkflowFormData['workflow_steps'][0]['workflow_step_departments'][0]['work_items']>[0];
  index: number;
  onRemove: (templateId: string) => void;
  onConditionToggle: (isConditional: boolean) => void;
  onConditionUpdate: (conditionRules: ConditionRules | undefined) => void;
}> = ({ workItem, index, onRemove, onConditionToggle, onConditionUpdate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: workItem.template_id || 'fallback-id' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [showConditionBuilder, setShowConditionBuilder] = useState(false);

  // Safety check for template_id
  if (!workItem.template_id) {
    console.error('SortableWorkItemItem: template_id is missing', workItem);
    return null;
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="p-3 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
      >
        {/* Main Content Row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab hover:cursor-grabbing text-gray-400 flex-shrink-0"
            >
              <GripVertical className="h-4 w-4" />
            </div>
            <Badge variant="outline" className="text-xs w-7 h-7 flex items-center justify-center p-0 flex-shrink-0">
              {index + 1}
            </Badge>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{workItem.template_name}</div>
              {workItem.template_description && (
                <div className="text-xs text-gray-600 line-clamp-2 mt-1">{workItem.template_description}</div>
              )}
            </div>
          </div>
          
          {/* Controls Section */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Conditional Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id={`conditional-${workItem.template_id}`}
                checked={workItem.is_conditional || false}
                onCheckedChange={(checked) => {
                  onConditionToggle(checked);
                  if (checked && (!workItem.condition_rules || workItem.condition_rules.conditions.length === 0)) {
                    setShowConditionBuilder(true);
                  }
                }}
              />
              <Label 
                htmlFor={`conditional-${workItem.template_id}`} 
                className="text-xs cursor-pointer whitespace-nowrap"
              >
                條件執行
              </Label>
            </div>
            
            {workItem.is_conditional && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowConditionBuilder(true)}
                className="h-8 px-2"
              >
                <Filter className="h-3 w-3 mr-1" />
                設定條件
                {workItem.condition_rules && workItem.condition_rules.conditions.length > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs px-1 h-5">
                    {workItem.condition_rules.conditions.length}
                  </Badge>
                )}
              </Button>
            )}
            
            <Button variant="ghost" size="sm" onClick={() => onRemove(workItem.template_id)} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Condition Builder Modal */}
      {showConditionBuilder && workItem.is_conditional && (
        <div className="mt-2">
          <ConditionBuilder
            conditionRules={workItem.condition_rules}
            onChange={(rules) => {
              onConditionUpdate(rules);
              if (!rules || rules.conditions.length === 0) {
                setShowConditionBuilder(false);
              }
            }}
            onClose={() => setShowConditionBuilder(false)}
          />
        </div>
      )}
    </>
  );
};

// 部門項目組件 (不可拖拽)
const DepartmentItem: React.FC<{
  department: WorkflowFormData['workflow_steps'][0]['workflow_step_departments'][0];
  index: number;
  stepId: string;
  onRemove: (id: string) => void;
  onExecutionOrderChange: (deptId: string, newOrder: number) => void;
  onWorkItemAdd: (stepId: string, deptId: string, workItem: { id: string; name: string; description?: string }) => void;
  onWorkItemRemove: (stepId: string, deptId: string, templateId: string) => void;
  onWorkItemReorder: (stepId: string, deptId: string, activeId: string, overId: string) => void;
  onWorkItemConditionToggle: (stepId: string, deptId: string, templateId: string, isConditional: boolean) => void;
  onWorkItemConditionUpdate: (stepId: string, deptId: string, templateId: string, conditionRules: ConditionRules | undefined) => void;
}> = ({ department, index, stepId, onRemove, onExecutionOrderChange, onWorkItemAdd, onWorkItemRemove, onWorkItemReorder, onWorkItemConditionToggle, onWorkItemConditionUpdate }) => {

  // Define colors for different parallel groups
  const groupColors = [
    'bg-blue-100 border-blue-300',
    'bg-green-100 border-green-300', 
    'bg-purple-100 border-purple-300',
    'bg-orange-100 border-orange-300',
    'bg-pink-100 border-pink-300',
    'bg-indigo-100 border-indigo-300'
  ];
  
  const colorClass = groupColors[(department.execution_order - 1) % groupColors.length];
  
  // Fetch work item templates for this department
  const { data: workItemTemplates = [], isLoading: templatesLoading } = useWorkItemTemplates(department.department_id);

  // Setup drag sensors (must be at top level, not conditional)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle work item drag end
  const handleWorkItemDragEnd = (event: DragEndEvent) => {
    try {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        onWorkItemReorder(stepId, department.department_id, active.id as string, over.id as string);
      }
    } catch (error) {
      console.error('Error in work item drag end:', error);
    }
  };

  return (
    <div className={`p-3 border-2 rounded-lg ${colorClass}`}>
      <div className="space-y-4">
        {/* Department Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Label className="text-xs text-gray-600">並行組:</Label>
              <Input
                type="number"
                min="1"
                value={department.execution_order}
                onChange={(e) => {
                  const newOrder = parseInt(e.target.value) || 1;
                  onExecutionOrderChange(department.department_id, newOrder);
                }}
                className="w-16 h-7 text-xs"
              />
            </div>
            <span className="font-medium text-sm">{department.department_name}</span>
            <Badge variant={department.is_required ? "default" : "secondary"} className="text-xs">
              {department.is_required ? "必要" : "選擇性"}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onRemove(department.department_id)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Work Item Selection - Enhanced Layout */}
        <div className="ml-4 mt-4 space-y-4">
          <div>
            <Label className="text-xs font-medium mb-2 block">選擇工作項目</Label>
            <Select
              disabled={templatesLoading}
              value=""
              onValueChange={(templateId) => {
                try {
                  const template = workItemTemplates.find(t => t.id === templateId);
                  
                  if (template) {
                    onWorkItemAdd(stepId, department.department_id, {
                      id: template.id,
                      name: template.name || '未命名工作項目',
                      description: template.description || undefined
                    });
                  }
                } catch (error) {
                  console.error('Error adding work item:', error);
                }
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder={templatesLoading ? "載入中..." : "選擇工作項目"} />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  // Filter out already selected work items
                  const selectedTemplateIds = (department.work_items || []).map(item => item.template_id);
                  const availableTemplates = (workItemTemplates || []).filter(template => 
                    !selectedTemplateIds.includes(template.id)
                  );
                  
                  if (availableTemplates.length === 0) {
                    return (
                      <div className="p-2 text-sm text-gray-500 text-center">
                        {(workItemTemplates || []).length === 0 
                          ? "沒有工作項目可選擇" 
                          : "所有工作項目已選擇"
                        }
                      </div>
                    );
                  }
                  
                  return availableTemplates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name || '未命名工作項目'}
                    </SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Work Items - Draggable List with Better Layout */}
          <div>
            <Label className="text-sm font-medium mb-3 block">已選工作項目 - 可拖拽排序</Label>
            {!department.work_items || department.work_items.length === 0 ? (
              <div className="text-sm text-gray-500 p-4 border-2 border-dashed rounded-lg text-center">
                尚未選擇工作項目
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleWorkItemDragEnd}
              >
                <SortableContext
                  items={(department.work_items || []).map(item => item.template_id).filter(Boolean)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {department.work_items.filter(item => item.template_id).map((workItem, workItemIndex) => (
                      <SortableWorkItemItem
                        key={workItem.template_id}
                        workItem={workItem}
                        index={workItemIndex}
                        onRemove={(templateId) => onWorkItemRemove(stepId, department.department_id, templateId)}
                        onConditionToggle={(isConditional) => onWorkItemConditionToggle(stepId, department.department_id, workItem.template_id, isConditional)}
                        onConditionUpdate={(conditionRules) => onWorkItemConditionUpdate(stepId, department.department_id, workItem.template_id, conditionRules)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 部門配置步驟
const DepartmentsStep: React.FC<{
  workflow_steps: WorkflowFormData['workflow_steps'];
  availableDepartments: any[];
  departmentsLoading: boolean;
  onDepartmentAdd: (stepId: string, department: { id: string; name: string; isRequired: boolean }) => void;
  onDepartmentRemove: (stepId: string, deptId: string) => void;
  onExecutionOrderChange: (stepId: string, deptId: string, newOrder: number) => void;
  onWorkItemAdd: (stepId: string, deptId: string, workItem: { id: string; name: string; description?: string }) => void;
  onWorkItemRemove: (stepId: string, deptId: string, templateId: string) => void;
  onWorkItemReorder: (stepId: string, deptId: string, activeId: string, overId: string) => void;
  onWorkItemConditionToggle: (stepId: string, deptId: string, templateId: string, isConditional: boolean) => void;
  onWorkItemConditionUpdate: (stepId: string, deptId: string, templateId: string, conditionRules: ConditionRules | undefined) => void;
  onNext: () => void;
  onPrevious: () => void;
}> = ({ workflow_steps, availableDepartments, departmentsLoading, onDepartmentAdd, onDepartmentRemove, onExecutionOrderChange, onWorkItemAdd, onWorkItemRemove, onWorkItemReorder, onWorkItemConditionToggle, onWorkItemConditionUpdate, onNext, onPrevious }) => {
  
  // 移除部門
  const handleRemoveDepartment = (stepId: string, deptId: string) => {
    onDepartmentRemove(stepId, deptId);
  };

  // 按並行組自動排序部門
  const getSortedDepartments = (departments: WorkflowFormData['workflow_steps'][0]['workflow_step_departments']) => {
    return [...departments].sort((a, b) => a.execution_order - b.execution_order);
  };
  return (
    <div className="space-y-6">
      {workflow_steps.map((step, index) => (
        <Card key={step.id}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge>{index + 1}</Badge>
              {step.name} - 部門配置與順序
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">{step.description}</p>
            
            <div className="space-y-6">
              {/* 部門選擇 - 縮小空間 */}
              <div className="max-w-md">
                <h4 className="font-medium mb-2">添加部門</h4>
                <Select 
                  disabled={departmentsLoading}
                  value="" // Always reset to empty after selection
                  onValueChange={(deptId) => {
                    const dept = availableDepartments.find(d => d.id === deptId);
                    if (dept) {
                      onDepartmentAdd(step.id, {
                        id: dept.id,
                        name: dept.name,
                        isRequired: true
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={departmentsLoading ? "載入中..." : "選擇部門"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDepartments
                      .filter(dept => !step.workflow_step_departments.some(d => d.department_id === dept.id))
                      .length === 0 ? (
                      <div className="p-2 text-sm text-gray-500 text-center">
                        沒有更多部門可選擇
                      </div>
                    ) : (
                      availableDepartments
                        .filter(dept => !step.workflow_step_departments.some(d => d.department_id === dept.id))
                        .map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* 已選部門 - 給更多空間給工作項目 */}
              <div>
                <h4 className="font-medium mb-2">已選部門與工作項目</h4>
                {step.workflow_step_departments.length === 0 ? (
                  <p className="text-sm text-gray-500 p-4 border-2 border-dashed rounded-lg">
                    尚未選擇部門
                  </p>
                ) : (
                  <div className="space-y-4">
                    {getSortedDepartments(step.workflow_step_departments).map((dept, deptIndex) => (
                      <DepartmentItem
                        key={dept.department_id}
                        department={dept}
                        index={deptIndex}
                        stepId={step.id}
                        onRemove={(id) => handleRemoveDepartment(step.id, id)}
                        onExecutionOrderChange={(deptId, newOrder) => onExecutionOrderChange(step.id, deptId, newOrder)}
                        onWorkItemAdd={onWorkItemAdd}
                        onWorkItemRemove={onWorkItemRemove}
                        onWorkItemReorder={onWorkItemReorder}
                        onWorkItemConditionToggle={onWorkItemConditionToggle}
                        onWorkItemConditionUpdate={onWorkItemConditionUpdate}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* 並行組說明 */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <h5 className="font-medium text-sm text-blue-800 mb-1">並行組說明</h5>
              <p className="text-xs text-blue-600">
                相同並行組編號的部門可以同時執行。不同組別按順序執行。使用顏色區分不同並行組。
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* Validation messages */}
      {workflow_steps.some(step => step.workflow_step_departments.length === 0) && (
        <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-1 bg-amber-600 rounded-full"></div>
            請為每個步驟至少選擇一個部門才能繼續
          </div>
        </div>
      )}
      
      {workflow_steps.some(step => 
        step.workflow_step_departments.some(dept => (dept.work_items || []).length === 0)
      ) && (
        <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-1 bg-amber-600 rounded-full"></div>
            請為每個部門至少選擇一個工作項目才能繼續
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          上一步
        </Button>
        <Button 
          onClick={onNext}
          disabled={
            workflow_steps.some(step => step.workflow_step_departments.length === 0) ||
            workflow_steps.some(step => 
              step.workflow_step_departments.some(dept => (dept.work_items || []).length === 0)
            )
          }
        >
          下一步 <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// 工作項目預覽組件 - 顯示已選擇的工作項目
const WorkItemsPreview: React.FC<{ 
  selectedWorkItems: WorkflowFormData['workflow_steps'][0]['workflow_step_departments'][0]['work_items'] 
}> = ({ selectedWorkItems }) => {
  if (!selectedWorkItems || selectedWorkItems.length === 0) {
    return (
      <div className="ml-6 space-y-2">
        <p className="text-sm text-gray-500">尚未選擇工作項目</p>
      </div>
    );
  }

  return (
    <div className="ml-6 space-y-2">
      {selectedWorkItems
        .sort((a, b) => a.execution_order - b.execution_order) // Sort by execution order
        .map((workItem, index) => (
        <div key={workItem.template_id} className="p-2 bg-gray-50 rounded flex items-center gap-2">
          <Badge variant="outline" className="text-xs w-6 h-6 flex items-center justify-center p-0">
            {workItem.execution_order}
          </Badge>
          <div className="flex-1">
            <div className="text-sm font-medium">{workItem.template_name}</div>
            {workItem.template_description && (
              <div className="text-xs text-gray-600">{workItem.template_description}</div>
            )}
          </div>
          {workItem.is_conditional && (
            <div className="flex items-center gap-1">
              <Filter className="h-3 w-3 text-gray-500" />
              <span className="text-xs text-gray-500">
                條件執行
                {workItem.condition_rules && workItem.condition_rules.conditions.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs px-1">
                    {workItem.condition_rules.conditions.length}
                  </Badge>
                )}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// 預覽步驟
const PreviewStep: React.FC<{
  formData: WorkflowFormData;
  isSubmitting?: boolean;
  editMode?: boolean;
  onSubmit: () => void;
  onPrevious: () => void;
}> = ({ formData, isSubmitting = false, editMode = false, onSubmit, onPrevious }) => {
  return (
    <div className="space-y-6">
      {/* 基本資訊 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            基本資訊
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div>
              <span className="font-medium">工作流程名稱：</span>
              <span className="text-gray-600">{formData.name}</span>
            </div>
            <div>
              <span className="font-medium">流程描述：</span>
              <span className="text-gray-600">{formData.description || '無描述'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 工作流程階段/部門/工序設定 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cog className="h-5 w-5" />
            工作流程階段/部門/工序設定
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {formData.workflow_steps.map((step, stepIndex) => (
              <div key={step.id} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-sm font-bold">
                    步驟 {stepIndex + 1}
                  </Badge>
                  <span className="font-medium text-lg">{step.name}</span>
                  {step.description && (
                    <span className="text-sm text-gray-500">- {step.description}</span>
                  )}
                </div>
                
                {/* 部門執行順序視覺化 */}
                <div className="ml-6 space-y-4">
                  <div className="border-l-4 pl-4 py-2 rounded-r border-blue-500 bg-blue-50">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className="text-xs border-blue-500 text-blue-700">
                        並行組
                      </Badge>
                    </div>
                    
                    <div className="grid gap-3">
                      {(() => {
                        // Group departments by parallel group (execution order)
                        const groupedDepts = step.workflow_step_departments.reduce((groups, dept) => {
                          const group = dept.execution_order;
                          if (!groups[group]) groups[group] = [];
                          groups[group].push(dept);
                          return groups;
                        }, {} as Record<number, typeof step.workflow_step_departments>);
                        
                        // Define colors for different parallel groups
                        const groupColors = [
                          'bg-blue-50 border-blue-200',
                          'bg-green-50 border-green-200', 
                          'bg-purple-50 border-purple-200',
                          'bg-orange-50 border-orange-200',
                          'bg-pink-50 border-pink-200',
                          'bg-indigo-50 border-indigo-200'
                        ];
                        
                        // Sort by parallel group number and render
                        return Object.keys(groupedDepts)
                          .map(Number)
                          .sort((a, b) => a - b)
                          .map((group, index) => (
                            <div key={group} className={`border rounded-lg p-3 ${groupColors[index % groupColors.length]}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="secondary" className="text-xs">
                                  並行組: {group}
                                </Badge>
                                <span className="text-xs text-gray-600">
                                  {groupedDepts[group].length > 1 ? '同組可並行執行' : ''}
                                </span>
                              </div>
                              
                              {groupedDepts[group].map(dept => (
                                <div key={dept.department_id} className="mb-3 last:mb-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Building2 className="h-4 w-4 text-gray-500" />
                                    <span className="font-medium text-sm">{dept.department_name}</span>
                                  </div>
                                  <WorkItemsPreview selectedWorkItems={dept.work_items} />
                                </div>
                              ))}
                            </div>
                          ));
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          上一步
        </Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          <Check className="mr-2 h-4 w-4" />
          {isSubmitting 
            ? (editMode ? '更新中...' : '創建中...') 
            : (editMode ? '更新工作流程' : '創建工作流程')
          }
        </Button>
      </div>
    </div>
  );
};
